'use client';

import { useEffect, type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { FilterSettingsFragmentFragmentDoc } from '@packages/gql/generated/shopping/FilterSettingsFragmentFragmentDoc';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';

import { useSearchFilters } from '../../hooks/useFilters';
import {
  type FilterSettingsMobileProps,
  FilterSettingsMobile,
} from '../FilterSettingsMobile/FilterSettingsMobile.client';
import { FilterSettingsDesktop } from '../FilterSettingsDesktop/FilterSettingsDesktop';
import { SelectedFilterValueList } from '../SelectedFilterValueList/SelectedFilterValueList';

/* GraphQL */ `
  fragment FilterSettingsFragment on SearchFilterableAndSortableResult {
    ...UseSearchFiltersFragment
    ...SelectedFilterValueListFragment
    ...FilterSettingsDesktopFragment
    ...FilterSettingsMobileFilterDialogButtonFragment
  }
`;

const messages = defineMessages({
  eraseFiltersNotification: {
    id: 'eraseFilters.notification',
    defaultMessage:
      'Die ausgewählten Filter der vorherigen Seite wurden entfernt, um Dir Artikel anbieten zu können. Nutze bei Bedarf erneut die Filterauswahl.',
  },
});

export type RenderCategoryDialogContent = (closeDialog: () => void) => React.ReactNode;

export type FilterSettingsProps = {
  maskedData: FragmentType<typeof FilterSettingsFragmentFragmentDoc>;
  showRemovedFiltersNotification?: boolean;
  renderCategoryDialogContent: RenderCategoryDialogContent;
};

/**
 * Responsive component to handle all filter interactions
 * */
export const FilterSettings: FC<FilterSettingsProps> = ({
  maskedData,
  showRemovedFiltersNotification,
  renderCategoryDialogContent,
}) => {
  const data = unmask<typeof FilterSettingsFragmentFragmentDoc>(maskedData);

  const { selectedFilterValues, pushFilterChange } = useSearchFilters(data);
  const { formatMessage } = useIntl();
  const { addNotification } = useNotification();

  useEffect(() => {
    if (showRemovedFiltersNotification) {
      addNotification({
        message: formatMessage(messages.eraseFiltersNotification),
        severity: 'warning',
        duration: 7000,
      });
      pushFilterChange([]);
    }
  }, [addNotification, formatMessage, pushFilterChange, showRemovedFiltersNotification]);

  return (
    <>
      <div className="hidden md:block">
        <FilterSettingsDesktop
          maskedData={data}
          selectedFilterValues={selectedFilterValues}
          onSubmit={pushFilterChange}
        />
      </div>
      <div className="md:hidden">
        <FilterSettingsMobile
          maskedData={data}
          selectedFilterValues={selectedFilterValues}
          onSubmit={pushFilterChange}
          renderCategoryDialogContent={renderCategoryDialogContent}
        />
      </div>
      <SelectedFilterValueList maskedData={data} showColors />
    </>
  );
};
